import { useState, useEffect, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import user from "../Models/user";
import axios from "axios";
import SalesIndex from "./SalesIndex";
import TableExample from "./TableExample";
import global from "../global";
import { numberFormat, priceFormat, showError } from "../utils/helper";
import ContentLoader from "react-content-loader";
import HeaderTop from "../Components/HeaderTop";
import AdminNoticesBar from "../Components/AdminNoticesBar";

export default function Check() {
  const { onCheckCompleted } = useOutletContext() || {};
  const [info, setInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // state for the check flow
  const [hasStarted, setHasStarted] = useState(false);
  const [checkId, setCheckId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [note, setNote] = useState("");

  // završetak i rezultat
  const [isFinished, setIsFinished] = useState(false);
  const [result, setResult] = useState(null);

  const [billsAndCoins, setBillsAndCoins] = useState([
    { value: 1, currency: "c", label: "1c", count: 0 },
    { value: 2, currency: "c", label: "2c", count: 0 },
    { value: 5, currency: "c", label: "5c", count: 0 },
    { value: 10, currency: "c", label: "10c", count: 0 },
    { value: 20, currency: "c", label: "20c", count: 0 },
    { value: 50, currency: "c", label: "50c", count: 0 },
    { value: 100, currency: "€", label: "1€", count: 0 },
    { value: 200, currency: "€", label: "2€", count: 0 },
    { value: 500, currency: "€", label: "5€", count: 0 },
    { value: 1000, currency: "€", label: "10€", count: 0 },
    { value: 2000, currency: "€", label: "20€", count: 0 },
    { value: 5000, currency: "€", label: "50€", count: 0 },
    { value: 10000, currency: "€", label: "100€", count: 0 },
    { value: 20000, currency: "€", label: "200€", count: 0 },
    { value: 50000, currency: "€", label: "500€", count: 0 },
  ]);

  const setMoneyBills = (index) => (e) => {
    const val = Number(e.target.value || 0);
    setBillsAndCoins((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], count: val };
      return next;
    });
  };

  // automatski izračun ukupno (€/100)
  const totalBillsAmount = useMemo(
    () => billsAndCoins.reduce((total, item) => total + item.count * item.value, 0) / 100,
    [billsAndCoins]
  );

  const filledFieldsCount = useMemo(
    () => billsAndCoins.filter((item) => item.count > 0).length,
    [billsAndCoins]
  );

  const MIN_FILLED_FIELDS = 5;

  const clearBills = () => {
    setBillsAndCoins((prev) => prev.map((i) => ({ ...i, count: 0 })));
  };

 const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetchUrl = `/reports/info`;
      const { data } = await window.axios.get(fetchUrl);

      setInfo(data?.data ?? null);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || "Greška pri dohvaćanju podataka.");
      setInfo(null);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchData();
  }, []);

  const infoReady = useMemo(() => {
    if (isLoading || !info) return false;
    const hasCash = typeof info.total_cash !== "undefined";
    const hasCc = typeof info.total_cc !== "undefined";
    return hasCash && hasCc;
  }, [isLoading, info]);

  const startCheck = async () => {
    try {
      if (!infoReady) {
        throw new Error("Podaci o blagajni još nisu učitani. Pokušajte ponovno za par sekundi.");
      }
      setStarting(true);

      const invoiceDeviceId = global?.invoiceDevice?.id ?? null;
      if (!invoiceDeviceId) throw new Error("Nedostaje invoice_device_id");

      const payload = {
        invoice_device_id: invoiceDeviceId,
        amount_cash: Number(info.total_cash ?? 0), // sada smo sigurni da info postoji
        amount_cc: Number(info.total_cc ?? 0),
        amount_deposit: Number(global?.invoiceDevice?.deposit ?? 0),
        started_at: new Date().toISOString(),
        note: note || null,
      };

      const { data } = await window.axios.post("/invoice-device-checks", payload);

      if (data?.success && data?.data?.id) {
        setCheckId(data.data.id);
        setHasStarted(true);
      } else if (data?.id) {
        setCheckId(data.id);
        setHasStarted(true);
      } else {
        throw new Error("Neočekivan odgovor API-a.");
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || e.message || "Greška pri pokretanju provjere.");
    } finally {
      setStarting(false);
    }
  };



  const finishCheck = async () => {
    try {
      if (!checkId) throw new Error("Nedostaje check ID.");

      if (filledFieldsCount < MIN_FILLED_FIELDS) {
        showError('Ponovno prebrojte novčanice i kovanice. Prebrojte novčanicu po novčanicu, kovanicu po kovanicu.');
        return;
      }

      setFinishing(true);

      const counted = totalBillsAmount; // već je svježe izračunat preko useMemo

      const payload = {
        amount_cash_counted: Number(counted),
        ended_at: new Date().toISOString(),
        note: note || null,
      };

      const { data } = await window.axios.put(`/invoice-device-checks/${checkId}`, payload);

      if (data?.success === false) throw new Error(data?.error || "Greška");

      // prikaži sažetak umjesto alert-a i zatvori unos
      setResult({
        counted,
        expected: Number(info?.total_cash || 0),
        difference: Number(counted || 0) - Number(info?.total_cash || 0),
        note,
      });
      setIsFinished(true);
      if (onCheckCompleted) onCheckCompleted();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || e.message || "Greška pri završavanju provjere.");
    } finally {
      setFinishing(false);
    }
  };

  /*
   * Brojanje ide "na slijepo": ni ocekivani iznos u blagajni ni izbrojeni zbroj
   * ne smiju se vidjeti dok blagajnica broji, inace samo upisuje komade dok ne
   * pogodi prikazani iznos. Sve se otkriva tek nakon "Zavrsi provjeru".
   */
  const revealAmounts = isFinished;
  const HIDDEN_AMOUNT = "•••••";

 return (
    <>
      <HeaderTop title={"Provjera blagajne"} />

      {/*
        Visina je omedena (height, ne min-height) da unutarnji sadrzaj moze skrolati.
        min-h-0 je nuzan jer flex stavka po defaultu ne moze biti niza od svog sadrzaja,
        pa se skrol nikad ne bi pojavio. Bez ovoga se na nizim ekranima (MacBook) donji
        dio desnog panela — napomena i gumb "Zavrsi provjeru" — jednostavno odreze.
      */}
      <div className="w-full flex flex-column" style={{ height: `calc(100vh - 80px)` }}>
        <div className="w-full flex-grow flex min-h-0">
          <div className="flex flex-col bg-blue-gray-50 h-full w-full min-h-0">
            <div className="h-full overflow-y-auto mt-4 flex items-start">
              {/* LEFT PANEL */}
              <div className="w-1/2 flex flex-col bg-blue-gray-50 h-full bg-white pr-2 pl-2 mb-4">
                <div className="bg-white rounded-sm flex flex-col h-fit shadow px-4 py-4 mb-4">
                  <div className="px-4 sm:px-0">
                    <h3 className="text-2xl font-semibold leading-7 text-gray-900">Gotovina u blagajni</h3>
                    <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                      {revealAmounts
                        ? "Ukupan iznosa gotovine u blagajni"
                        : "Iznosi se prikazuju tek nakon završetka provjere"}
                    </p>
                  </div>

                  {isLoading ? (
                    <div className="p-4 text-gray-500">Učitavam…</div>
                  ) : error ? (
                    <div className="p-4 text-red-600">
                      {error}
                      <button
                        className="ml-2 underline"
                        onClick={fetchData}
                      >
                        Pokušaj ponovno
                      </button>
                    </div>
                  ) : (
                    <div className="mt-6 border-t border-gray-100">
                      <dl className="divide-y divide-gray-100">
                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                          <dt className="text-sm font-medium leading-6 text-gray-900">Datum</dt>
                          <dd className="mt-1 text-sm leading-6 text-gray-700 font-bold">{info?.from ?? "-"}</dd>
                        </div>
                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                          <dt className="text-sm font-medium leading-6 text-gray-900">Gotovina</dt>
                          <dd className="mt-1 text-sm leading-6 text-gray-700 font-bold">
                            {revealAmounts
                              ? info?.payment_method_cash && priceFormat(info.payment_method_cash.total_with_tax)
                              : HIDDEN_AMOUNT}
                          </dd>
                        </div>
                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                          <dt className="text-sm font-medium leading-6 text-gray-900">Polog blagajne</dt>
                          <dd className="mt-1 text-sm leading-6 text-gray-700 font-bold">
                            {revealAmounts ? priceFormat(global?.invoiceDevice?.deposit || 0) : HIDDEN_AMOUNT}
                          </dd>
                        </div>
                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                          <dt className="leading-6 text-gray-900 font-bold text-xl">Ukupno</dt>
                          <dd className="mt-1 leading-6 text-gray-700 font-bold text-xl">
                            {revealAmounts ? priceFormat(info?.total_cash || 0) : HIDDEN_AMOUNT}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="w-1/2 flex flex-col bg-blue-gray-50 h-full bg-white pr-2 pl-2 mb-4">
                <div className="bg-white rounded-sm flex flex-col h-fit shadow px-4 py-4 mb-4">
                  <div className="px-4 sm:px-0">
                    <h3 className="text-2xl font-semibold leading-7 text-gray-900">Provjera blagajne</h3>
                    <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">Provjera gotovine u blagajni</p>
                  </div>

                  {/* Glavni render switch: sažetak → start → unos */}
                  {isFinished ? (
                    // SAŽETAK
                    <div className="p-6 text-center">
                      <h3 className="text-2xl font-bold text-green-600 mb-4">Provjera je obavljena ✅</h3>

                      <p className="text-lg mb-2">U blagajni: {priceFormat(result?.expected ?? 0)}</p>
                      <p className="text-lg mb-2">Izbrojeno: {priceFormat(result?.counted ?? 0)}</p>
                      <p
                        className={
                          "text-lg mb-2 font-bold " +
                          (Number(result?.difference ?? 0) < 0 ? "text-red-500" : "text-gray-700")
                        }
                      >
                        Razlika: {priceFormat(result?.difference ?? 0)}
                      </p>
                      {result?.note && <p className="italic text-gray-600">Bilješka: {result.note}</p>}

                      <button
                        onClick={() => {
                          setHasStarted(false);
                          setCheckId(null);
                          setIsFinished(false);
                          setResult(null);
                          clearBills();
                        }}
                        className="mt-6 px-4 py-2 rounded bg-primary hover:bg-yellow-600"
                      >
                        Nova provjera
                      </button>
                    </div>
                  ) : !hasStarted ? (
                    // START SEKCIJA
                    <div className="mt-4">
                      <button
                        onClick={startCheck}
                        disabled={starting || !infoReady} // ⬅️ bitno
                        className={
                          "font-bold text-base rounded-sm w-full py-3 mt-4 focus:outline-none " +
                          (starting || !infoReady
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-primary hover:bg-yellow-600 text-black")
                        }
                        title={!infoReady ? "Pričekajte da se podaci učitaju" : undefined}
                      >
                        {starting ? "Pokrećem..." : "Započni provjeru"}
                      </button>
                    </div>
                  ) : (
                    // UNOS NOVČANICA/KOŽICA
                    <div className="mt-4 border-t border-gray-100">
                      <dl className="divide-y divide-gray-100">
                        <div className="grid grid-cols-2 gap-2 mt-4">
                          {billsAndCoins.map((money, index) => (
                            <div key={money.value} className="relative">
                              <div className="g-gray-10 absolute inset-y-0 start-0 top-0 flex items-center ps-6 mr-7 pointer-events-none">
                                {money.label}
                              </div>
                              <input
                                onChange={setMoneyBills(index)}
                                type="number"
                                className="bg-gray-10 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full ps-20 p-2.5"
                                placeholder={"0"}
                                min={0}
                                value={money.count} // kontrolirani input
                              />
                            </div>
                          ))}
                          <button
                            className="text-blue-gray-300 hover:text-pink-500 focus:outline-none"
                            onClick={(e) => {
                              e.preventDefault();
                              clearBills();
                            }}
                            title="Očisti"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6 inline-block"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>

                        <hr className="mt-4" />

                        {/* Umjesto izbrojenog iznosa i razlike ide uputa — brojanje mora biti "na slijepo" */}
                        <div className="px-4 py-3">
                          <div className="rounded-md border border-yellow-400 bg-yellow-50 p-3 text-base leading-6 text-gray-800">
                            Poštovani, molimo da sav novac izbrojite komadno te upišete ukupno
                            izbrojeno komada u za to predviđeno polje, te nakon toga kliknete na
                            „Završi provjeru”!
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-2 mt-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              finishCheck();
                            }}
                            disabled={finishing || !checkId}
                            className={
                              "font-bold text-base rounded-sm w-full py-3 mt-2 focus:outline-none " +
                              (finishing || !checkId
                                ? "bg-gray-300 cursor-not-allowed"
                                : "bg-green-500 hover:bg-green-600 text-white")
                            }
                          >
                            {finishing ? "Spremam..." : "Završi provjeru"}
                          </button>
                        </div>
                      </dl>
                    </div>
                  )}
                </div>
              </div>
              {/* END RIGHT PANEL */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
